import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { PublisherResponse, CreatePublisher } from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/toast';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

interface FormData {
  name: string;
  email?: string;
  status: 'active' | 'inactive';
}

const initialFormState: FormData = {
  name: '',
  email: '',
  status: 'active',
};

export function Publishers() {
  const [publishers, setPublishers] = useState<PublisherResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const { success, error: toastError } = useToast();

  const loadPublishers = () => {
    api
      .getPublishers()
      .then(setPublishers)
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadPublishers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data: CreatePublisher = {
        name: formData.name,
        status: formData.status,
        ...(formData.email && { email: formData.email }),
      };

      await api.createPublisher(data);
      success('Publisher created successfully');
      setIsDialogOpen(false);
      setFormData(initialFormState);
      loadPublishers();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to create publisher');
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateDialog = () => {
    setFormData(initialFormState);
    setIsDialogOpen(true);
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading publishers: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Publishers</h1>
          <p className="text-muted-foreground mt-2">Manage your traffic publishers</p>
        </div>
        <Button onClick={openCreateDialog}>Create Publisher</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Publishers</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {publishers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                    No publishers found. Create your first publisher to get started.
                  </TableCell>
                </TableRow>
              ) : (
                publishers.map((publisher) => (
                  <TableRow key={publisher.id}>
                    <TableCell className="font-medium">{publisher.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {publisher.email || '-'}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          publisher.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {publisher.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Publisher</DialogTitle>
            <DialogDescription>
              Create a new publisher by filling in the details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Traffic Partner Inc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@publisher.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })
                  }
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
